import { usePostExternalCambioPassword } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  FormHandler,
  FormItem,
  SubmitButton,
  TextInput,
} from '@framework/form';
import { t } from '@framework/i18n';
import { LinkButton, useBreadcrumbItem, useHistory } from '@framework/ui';
import { yup } from '@framework/validation';
import { Card, Result, message } from 'antd';
import { Typography } from 'antd/lib';
import React, { useState } from 'react';
import { PageLayout } from '../../ui/layout/PageLayout';
const { Title, Paragraph } = Typography;

// Cambio password
export const citizenChangeSchema = yup
  .object({
    oldPassword: yup
      .string()
      .label(t('user.current-password', 'Password Attuale'))
      .required(),
    newPassword: yup
      .string()
      .label(t('user.new-password', 'Nuova Password'))
      .required(),
    confirmPassword: yup
      .string()
      .label(
        t('user.password-change.form.password-confirm', 'Conferma Password'),
      )
      .required()
      .when(['newPassword'], (values, schema) => {
        const newPassword = values[0];
        return schema.test({
          name: 'check-password',
          message: t(
            'user.le-password-non-coincidono',
            `Le password non coincidono.`,
          ),
          exclusive: true,
          test: value => {
            if (!value || typeof value !== 'string') return false;
            return value === newPassword;
          },
        });
      }),
  })
  .noUnknown()
  .required();

export interface IUserChangePasswordPageProps {}

/**
 * Pagina in cui un utente **loggato** può cambiare la propria password.
 */
export function UserChangePasswordPage(props: IUserChangePasswordPageProps) {
  const history = useHistory();

  useBreadcrumbItem({
    path: '/utente/profilo',
    title: 'Profilo',
  });
  useBreadcrumbItem({
    path: history.location.pathname,
    title: t('user.password-change.breadcrumb', 'Cambia Password'),
  });

  const handlePasswordChange = usePostExternalCambioPassword();
  const [success, setSuccess] = useState(false);

  if (handlePasswordChange.isError) {
    return (
      <Result
        status="error"
        title={t(
          'user.password-change.response.error.title',
          `Errore nel cambio della password`,
        )}
        subTitle={`${t(
          'user.password-change.response.error.sub-title',
          `Non è stato possibile reimpostare la password.`,
        )} ${getNetworkErrorMessage(
          handlePasswordChange.error,
          "Errore sconosciuto, contattare l'assistenza o riprovare più tardi",
        )}`}
        extra={
          <LinkButton
            type="primary"
            to="/utente/profilo"
            children={t(
              'user.password-change.response.success.submit-button',
              'Torna al Profilo',
            )}
          />
        }
      />
    );
  }

  return (
    <PageLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          {success ? (
            <Result
              status="success"
              title={t(
                'user.password-change.response.success.title',
                'Password modificata con successo',
              )}
              extra={
                <LinkButton
                  type="primary"
                  to="/"
                  children={t(
                    'user.password-change.response.success.back-to-inquiry',
                    'Torna alle Domande',
                  )}
                />
              }
            />
          ) : (
            <FormHandler
              schema={citizenChangeSchema}
              validateTrigger="onChange"
              onSubmit={async rawValues => {
                try {
                  await handlePasswordChange.mutateAsync({
                    data: rawValues,
                  });

                  message.success(t('user.password-change.success', 'Password modificata')); // prettier-ignore
                  setSuccess(true);
                } catch (e) {
                  console.error(e);
                  message.error(
                    t(
                      'user.password-change.error.message',
                      'Non è stato possibile cambiare la password.',
                    ),
                  );
                }
              }}
            >
              <Title level={3}>
                {t('user.password-change.title', 'Modifica password')}
              </Title>
              <Paragraph>
                {t(
                  'user.password-change.text',
                  `Inserisci la nuova password per l'utente`,
                )}
              </Paragraph>
              <FormItem
                label={t('user.current-password', 'Password Attuale')}
                name="oldPassword"
              >
                <TextInput type="password" name="oldPassword" />
              </FormItem>
              <FormItem
                label={t('user.new-password', 'Nuova Password')}
                name="newPassword"
              >
                <TextInput type="password" name="newPassword" />
              </FormItem>

              <FormItem
                name="confirmPassword"
                label={t(
                  'user.password-change.form.password-repeat',
                  'Ripeti Password',
                )}
              >
                <TextInput type="password" name="confirmPassword" />
              </FormItem>

              <SubmitButton
                pristineMessage={null}
                icon={null}
                style={{ width: '100%' }}
              >
                {t(
                  'user.password-change.form.submit-button',
                  'Cambia Password',
                )}
              </SubmitButton>
            </FormHandler>
          )}
        </Card>
      </div>
    </PageLayout>
  );
}
